import {
  collection,
  doc,
  FirestoreDataConverter,
  getDoc,
  limit,
  orderBy,
  query,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';
import { useCollectionData } from 'react-firebase-hooks/firestore';
import { auth, firestore } from '@/lib/firebase';
import type { Box } from './boxes';

/**
 * Append-only audit trail for the `boxes` collection (the fleet's source of
 * truth, published to the headend). Every mutation goes through the
 * audited*Box() helpers below, which commit the box write and its audit entry
 * in ONE WriteBatch — a change can't exist without its log entry.
 *
 * Rules enforce create-only (no update/delete for anyone), byUid == auth.uid
 * and at == request.time, so entries can't be forged, edited or backdated.
 */

/** Fields tracked in the log. Agent-written noise (status, versions) is not. */
export const AUDITED_FIELDS = [
  'name',
  'ip',
  'group',
  'tv',
  'brand',
  'icon',
] as const;
export type AuditedField = (typeof AUDITED_FIELDS)[number];

export type BoxAuditAction = 'create' | 'update' | 'delete';

export interface FieldChange {
  field: AuditedField;
  from: string;
  to: string;
}

export interface BoxAuditEntry {
  id?: string;
  at: Date | null;
  byUid: string;
  byEmail: string;
  boxId: string;
  /** Box name at the time of the change (after a rename, the new name). */
  boxName: string;
  action: BoxAuditAction;
  changes: FieldChange[];
  /** Full field set at deletion time — enough to re-create the box by hand. */
  snapshot?: Record<string, string> | null;
}

/** The audited subset of a box, everything coerced to trimmed strings. */
export type BoxFields = Partial<Record<AuditedField, string>>;

const val = (b: BoxFields | undefined, f: AuditedField) =>
  (b?.[f] ?? '').trim();

/** Field-level from → to diff over the audited fields. Empty = no-op. */
export function diffBox(prev: BoxFields, next: BoxFields): FieldChange[] {
  const changes: FieldChange[] = [];
  for (const field of AUDITED_FIELDS) {
    const from = val(prev, field);
    const to = val(next, field);
    if (from !== to) changes.push({ field, from, to });
  }
  return changes;
}

const converter: FirestoreDataConverter<BoxAuditEntry> = {
  toFirestore: () => {
    throw new Error('boxAudit is written only via the audited*Box helpers');
  },
  fromFirestore: (snapshot) => {
    const d = snapshot.data();
    return {
      id: snapshot.id,
      at: d.at?.toDate?.() ?? null,
      byUid: d.byUid ?? '',
      byEmail: d.byEmail ?? '',
      boxId: d.boxId ?? '',
      boxName: d.boxName ?? '',
      action: (d.action ?? 'update') as BoxAuditAction,
      changes: d.changes ?? [],
      snapshot: d.snapshot ?? null,
    };
  },
};

/** Latest audit entries, newest first. Filtering is client-side over this
 *  window — revisit with a where()+index if the log outgrows it. */
export function useBoxAudit(max = 300) {
  return useCollectionData(
    query(
      collection(firestore, 'boxAudit').withConverter(converter),
      orderBy('at', 'desc'),
      limit(max)
    ),
    { snapshotListenOptions: { includeMetadataChanges: false } }
  );
}

function actor() {
  const u = auth.currentUser;
  if (!u) throw new Error('Not signed in');
  return { byUid: u.uid, byEmail: u.email ?? '' };
}

function auditFields(box: BoxFields): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of AUDITED_FIELDS) out[f] = val(box, f);
  return out;
}

function stageEntry(
  batch: ReturnType<typeof writeBatch>,
  entry: Omit<BoxAuditEntry, 'id' | 'at' | 'byUid' | 'byEmail'>
) {
  batch.set(doc(collection(firestore, 'boxAudit')), {
    at: serverTimestamp(),
    ...actor(),
    ...entry,
  });
}

/**
 * Update a box. Commits, in one atomic batch: the boxes/{id} update, the
 * alertBoxes re-key when the IP changed (those docs are keyed by IP — without
 * the move a monitored box silently stops alerting), and the audit entry.
 * Returns { changed, movedAlert }; a no-op diff writes nothing.
 */
export async function auditedUpdateBox(
  box: Box,
  fields: BoxFields
): Promise<{ changed: boolean; movedAlert: boolean }> {
  if (!box.id) throw new Error('Box has no id');
  const next = { ...auditFields(box), ...auditFields(fields) };
  const changes = diffBox(box, next);
  if (!changes.length) return { changed: false, movedAlert: false };

  const batch = writeBatch(firestore);
  const update: Record<string, string> = {};
  for (const c of changes) update[c.field] = next[c.field];
  batch.update(doc(firestore, 'boxes', box.id), update);

  let movedAlert = false;
  if (next.ip !== box.ip.trim()) {
    const oldRef = doc(firestore, 'alertBoxes', box.ip);
    const snap = await getDoc(oldRef);
    if (snap.exists()) {
      batch.set(doc(firestore, 'alertBoxes', next.ip), {
        ip: next.ip,
        name: next.name,
        snoozeUntil: snap.data().snoozeUntil ?? null,
      });
      batch.delete(oldRef);
      movedAlert = true;
    }
  }

  stageEntry(batch, {
    boxId: box.id,
    boxName: next.name,
    action: 'update',
    changes,
  });
  await batch.commit();
  return { changed: true, movedAlert };
}

/** Create a box (status starts UNKNOWN; the agent's next cycle fills it in).
 *  The audit entry logs every field as '—' → value. */
export async function auditedCreateBox(fields: BoxFields): Promise<string> {
  const data = auditFields(fields);
  const batch = writeBatch(firestore);
  const ref = doc(collection(firestore, 'boxes'));
  batch.set(ref, { ...data, status: 'UNKNOWN' });
  stageEntry(batch, {
    boxId: ref.id,
    boxName: data.name,
    action: 'create',
    changes: AUDITED_FIELDS.filter((f) => data[f]).map((f) => ({
      field: f,
      from: '—',
      to: data[f],
    })),
  });
  await batch.commit();
  return ref.id;
}

/** Delete a box and, in the same batch, its IP-keyed alertBoxes/liveBoxes
 *  docs, so nothing is left monitoring an address that no longer exists.
 *  The audit entry keeps a full snapshot for manual restore. */
export async function auditedDeleteBox(box: Box): Promise<void> {
  if (!box.id) throw new Error('Box has no id');
  const snapshot = auditFields(box);
  const batch = writeBatch(firestore);
  batch.delete(doc(firestore, 'boxes', box.id));
  batch.delete(doc(firestore, 'alertBoxes', box.ip));
  batch.delete(doc(firestore, 'liveBoxes', box.ip));
  stageEntry(batch, {
    boxId: box.id,
    boxName: box.name,
    action: 'delete',
    changes: AUDITED_FIELDS.filter((f) => snapshot[f]).map((f) => ({
      field: f,
      from: snapshot[f],
      to: '—',
    })),
    snapshot,
  });
  await batch.commit();
}
